import { memo, useEffect, useRef } from 'react';
import NumberFlow from '@number-flow/react';
import Autoplay from 'embla-carousel-autoplay';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { readablePlatformsMap } from 'root/src/shared/constants/dropKeys';
import { Badge } from 'root/src/client/v2/common/components/ui/badge';
import { buildAssetUrl, resolveThumbnailPlaceholders } from 'root/src/client/v2/common/helpers/assetUrl';
import { Root, Container, Slide, useCarouselContext } from 'root/src/client/v2/common/components/Carousel/Carousel';
import { useCarouselAutoplay } from 'root/src/client/v2/common/hooks/useCarouselAutoplay';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from 'root/src/client/v2/common/components/ui/tooltip';
import { GamePlatformIcon } from 'root/src/client/v2/common/components/Games/components/GamePlatformIcon';
import { useCreatorGiveaways } from 'root/src/client/v2/_pages/creator/redesign/useCreatorGiveaways';
import { useCreatorGiveawayRewards } from 'root/src/client/v2/_pages/creator/redesign/useCreatorGiveawayRewards';
import { useCreatorGiveawayWon } from 'root/src/client/v2/_pages/creator/redesign/useCreatorGiveawayWon';
import { CreatorEnterGiveawayCta } from 'root/src/client/v2/_pages/creator/redesign/CreatorEnterGiveawayCta';
import { Img } from 'root/src/client/v2/common/components/ui/image';
// Card width is pinned in px rather than rem so the row stays identical if the root font size ever
// changes. The extra 16px is the pl-4 gutter that -ml-4 on the container cancels out.
const cardWidth = 208;
const slideWidth = `${cardWidth + 16}px`;
// The art header is full-bleed, so it spans the card's inner width (the 1px borders aside). Its height
// is derived from that rather than pinned, so the header stays at the box art's native 3:4 whenever the
// card width changes and cover keeps cropping nothing.
const artHeight = Math.round(((cardWidth - 2) * 4) / 3);
const autoplayDelay = 3500;
const arrowClass = 'flex h-8 w-8 items-center justify-center rounded-lg border border-solid border-white/[0.08] bg-secondary/60 text-foreground transition-colors hover:bg-secondary disabled:opacity-30';
// Twitch box art is natively 3:4, but the shared reward mapper hands us `image` already substituted
// to a square 192x192, which object-cover then crops to a visibly different scale than the uploaded
// images on code rewards. Re-resolve the game template at the frame's own ratio; rewards without a
// game (code rewards) keep their stored image.
const rewardImageSrc = (reward) => {
    var _a, _b;
    return (_b = resolveThumbnailPlaceholders((_a = reward.game) === null || _a === void 0 ? void 0 : _a.boxArtTemplateUrl, 285, 380)) !== null && _b !== void 0 ? _b : buildAssetUrl(reward.image, { w: 320, q: 80, fmt: 'webp' });
};
// Mirrors the autoplay timer so the row shows how long is left before it advances. Driven by rAF
// writing straight to the node: a 60fps setState would re-render every card on the row instead.
const AutoplayProgress = memo(() => {
    const { emblaApi } = useCarouselContext();
    const barRef = useRef(null);
    useEffect(() => {
        const autoplay = emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.plugins().autoplay;
        if (!autoplay) {
            return undefined;
        }
        let frame = 0;
        const tick = () => {
            const node = barRef.current;
            if (node) {
                // timeUntilNext is null while autoplay is paused on hover, which parks the bar at empty.
                const timeUntilNext = autoplay.timeUntilNext();
                const elapsed = timeUntilNext === null ? 0 : autoplayDelay - timeUntilNext;
                node.style.transform = `scaleX(${Math.min(Math.max(elapsed / autoplayDelay, 0), 1)})`;
            }
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [emblaApi]);
    return (<div className="h-1 w-16 overflow-hidden rounded-full bg-white/10">
			<div ref={barRef} className="h-full w-full origin-left scale-x-0 rounded-full bg-gradient-to-r from-neon-cyan to-neon-pink"/>
		</div>);
});
// Own arrows rather than the shared Carousel ones so a manual step restarts the countdown. Embla does
// not re-arm its timer for a programmatic scroll, so clicking an arrow late in the 3.5s window used to
// be overtaken by the timer: the row slid on again immediately and the progress bar above sat full.
const RewardArrows = memo(() => {
    const { emblaApi, resetAutoplay } = useCarouselAutoplay();
    const scrollPrev = () => {
        resetAutoplay();
        emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.scrollPrev();
    };
    const scrollNext = () => {
        resetAutoplay();
        emblaApi === null || emblaApi === void 0 ? void 0 : emblaApi.scrollNext();
    };
    return (<>
			<button type="button" onClick={scrollPrev} aria-label="Previous rewards" className={arrowClass}>
				<ChevronLeftIcon sx={{ fontSize: 18 }}/>
			</button>
			<button type="button" onClick={scrollNext} aria-label="Next rewards" className={arrowClass}>
				<ChevronRightIcon sx={{ fontSize: 18 }}/>
			</button>
		</>);
});
export const CreatorRewardSection = memo(({ creator }) => {
    var _a, _b, _c, _d, _e;
    const { active, resolved } = useCreatorGiveaways(creator.userId);
    const { rewards } = useCreatorGiveawayRewards(creator.userId, creator.mnemonicId);
    useCreatorGiveawayWon({
        resolved: resolved,
        creatorUserId: creator.userId,
        creatorMnemonicId: creator.mnemonicId,
    });
    const lastResolved = resolved[0];
    const winnerName = (_c = (_b = (_a = lastResolved === null || lastResolved === void 0 ? void 0 : lastResolved.vouchers) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.owner) === null || _c === void 0 ? void 0 : _c.name;
    const drawnOn = (lastResolved === null || lastResolved === void 0 ? void 0 : lastResolved.endDate) ? new Date(lastResolved.endDate).toLocaleDateString() : undefined;
    const entries = (_e = (_d = active === null || active === void 0 ? void 0 : active.stats) === null || _d === void 0 ? void 0 : _d.entryCount) !== null && _e !== void 0 ? _e : 0;
    const activeGiveaway = active;
    if (!rewards.length && !winnerName && !active) {
        return null;
    }
    // Autoplay pauses on hover so a reward can be read without the row sliding away, and resumes on leave.
    const hasCarousel = rewards.length > 1;
    const autoplayPlugins = hasCarousel
        ? [Autoplay({ delay: autoplayDelay, stopOnInteraction: false, stopOnMouseEnter: true })]
        : [];
    return (<section className="mt-6">
			<div className="grid gap-6 lg:grid-cols-5">
				<div className="glass rounded-2xl border border-solid border-white/[0.08] p-6 lg:col-span-3">
					<div className="mb-5 flex items-center gap-3">
						<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-neon-amber to-neon-pink">
							<EmojiEventsIcon sx={{ fontSize: 16 }} className="text-white"/>
						</div>
						<h2 className="font-display text-base font-bold text-foreground">Reward Winner</h2>
					</div>
					<div className="rounded-xl bg-secondary/60 p-5 text-sm">
						{winnerName ? (<div className="flex flex-wrap items-center gap-x-8 gap-y-3">
								<div>
									<p className="text-xs text-muted-foreground">Winner</p>
									<p className="font-semibold text-foreground">{winnerName}</p>
								</div>
								{drawnOn && (<div>
										<p className="text-xs text-muted-foreground">Drawn on</p>
										<p className="font-medium text-foreground">{drawnOn}</p>
									</div>)}
							</div>) : (<p className="text-muted-foreground">No giveaway winner drawn yet.</p>)}
					</div>
				</div>

				<div className="glass flex flex-col items-center justify-center gap-4 rounded-2xl border border-solid border-white/[0.08] border-t-2 border-t-neon-cyan/30 p-5 lg:col-span-2">
					<div className="flex flex-col items-center">
						<p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Giveaway entries</p>
						<p className="mt-2 font-display text-3xl font-bold text-foreground"><NumberFlow value={entries}/></p>
					</div>
					<CreatorEnterGiveawayCta giveaway={activeGiveaway} creatorUserId={creator.userId}/>
				</div>
			</div>

			{rewards.length > 0 && (<Root loop={hasCarousel} align="start" dragFree plugins={autoplayPlugins} className="mt-8">
					{/* One provider for the whole section: the heading's info tooltip and every card title's
            tooltip live under it. */}
					<TooltipProvider delayDuration={200}>
						<div className="mb-4 flex items-center justify-between gap-4">
							<div className="flex items-center gap-2.5">
								<h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Reward Giveaway Options</h3>
								<span className="flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-neon-cyan/15 px-1.5 font-display text-[11px] font-bold leading-none text-neon-cyan">
									<NumberFlow value={rewards.length}/>
								</span>
								<Tooltip>
									<TooltipTrigger asChild>
										<button type="button" aria-label="What are reward giveaway options?" className="flex text-muted-foreground/60 transition-colors hover:text-muted-foreground">
											<InfoOutlinedIcon sx={{ fontSize: 14 }}/>
										</button>
									</TooltipTrigger>
									<TooltipContent className="max-w-[18rem]">
										Game keys and codes this creator has set aside for their fans. Enter the giveaway for a chance to
										win one of them when the next winner is drawn.
									</TooltipContent>
								</Tooltip>
							</div>
							{hasCarousel && (<div className="flex items-center gap-3">
									<AutoplayProgress />
									<RewardArrows />
								</div>)}
						</div>
						{/* select-none: the row is drag-scrollable, so without it a drag sweeps a text selection
            across the card titles instead of moving the carousel. */}
						<Container className="-ml-4 flex select-none">
							{rewards.map((reward) => {
                const imageSrc = rewardImageSrc(reward);
                return (
                // min-w-0 is load-bearing. A flex item defaults to min-width:auto, so it cannot shrink below
                // its min-content, and the card's `truncate` title sets white-space:nowrap, which makes that
                // min-content the full reward name. Slides were therefore pushed past their flex-basis by however
                // long each name was, giving every card (and every percentage width inside it) a different width.
                <Slide key={reward.id} width={slideWidth} className="min-w-0 pl-4">
										{/* Deliberately not a flex column: every card is already the same height by construction
                (fixed-height art, one-line title, one-line project slot), and `flex` would only pull in
                the legacy `.flex { flex: 1 }` rule for nothing. */}
										<div className="glass w-full rounded-2xl border border-solid border-white/[0.08] p-3">
											{/* Full-bleed cover header, pulled out of the card's padding by negative margins (same trick as the
                dare modal's hero). The art spans the card edge to edge, so there is no inset frame to centre and no
                pinned width tied to the padding, while the derived height keeps every card identical regardless of
                the source image. */}
											<div style={{ height: artHeight }} className="relative -mx-3 -mt-3 mb-3 w-[calc(100%+1.5rem)] overflow-hidden rounded-t-2xl bg-secondary">
												{imageSrc ? (
                    // cover, not contain: the pool mixes source aspect ratios, and contain fits each source
                    // whole, so a square one letterboxes and reads as visibly smaller than a native 3:4 one.
                    // Cover makes every image occupy the header exactly; the header is 3:4 and rewardImageSrc
                    // requests game art at 3:4 too, so covering box art crops nothing.
                    <Img src={imageSrc} alt={reward.name} className="h-full w-full object-cover"/>) : (
                    // Placeholder for rewards with no artwork at all, so the header never reads as a broken
                    // or half-loaded card. Fills the same box, so card heights are unaffected.
                    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-secondary to-card">
														<CardGiftcardIcon sx={{ fontSize: 40 }} className="text-muted-foreground/40"/>
													</div>)}
											</div>
											{/* The title is truncated to one line to keep cards uniform, so the full name is only
                reachable on hover. asChild keeps the h4 as the trigger element rather than nesting a button. */}
											<Tooltip>
												<TooltipTrigger asChild>
													<h4 className="truncate font-display text-sm font-semibold text-foreground">{reward.name}</h4>
												</TooltipTrigger>
												<TooltipContent className="max-w-[16rem]">{reward.name}</TooltipContent>
											</Tooltip>
											{/* Always rendered and clamped to one line, reserving the slot even when empty: a missing or
                wrapping project title is the only thing that would make a card taller than its neighbours. */}
											<p className="mt-1 line-clamp-1 min-h-[1rem] text-xs text-neon-cyan">{reward.projectTitle}</p>
											{/* Platform badge instead of the rewardFor label, which only ever read "fans" / "creator".
                Always rendered (code rewards carry no platform, so they fall back to a generic gift) to keep
                every card the same height. max-w-full + truncate stop a long name like "Epic Games Store"
                from pushing the badge past the card. */}
											<Badge variant="secondary" className="mt-2.5 max-w-full gap-1.5 px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
												{reward.platform
                        ? <GamePlatformIcon platform={reward.platform} className="shrink-0" style={{ fontSize: 12 }}/>
                        : <CardGiftcardIcon sx={{ fontSize: 12 }} className="shrink-0"/>}
												<span className="truncate">{reward.platform ? readablePlatformsMap[reward.platform] : 'Reward'}</span>
											</Badge>
										</div>
									</Slide>);
            })}
						</Container>
					</TooltipProvider>
				</Root>)}
		</section>);
});
